/**
 * useKeyboardShortcuts Hook
 * Global keyboard event listener for application-wide shortcuts
 *
 * Shortcuts:
 * - Space: Play/pause toggle (pure - always resumes/pauses)
 * - Enter: Play from selected track forward
 * - Arrow Left/Right: Previous/next track (hookmode-aware)
 * - Arrow Up/Down: Select previous/next track in list
 * - H: Toggle hookmode
 * - Delete/Backspace: Remove selected tracks
 * - Cmd/Ctrl+A: Select all tracks
 * - Cmd/Ctrl+Z: Undo
 * - Cmd/Ctrl+Shift+Z: Redo
 * - Cmd/Ctrl+F: Focus search input
 * - 1/2/3: Switch right panel tabs
 * - Cmd/Ctrl+R: Open reorder modal
 * - Escape: Clear selection / close modals
 * - ?: Show keyboard shortcuts
 */

import { useEffect } from 'react';
import { useWorkbenchStore } from '../stores/workbench';
import { useSelectionStore } from '../stores/selection';
import { useUIStore } from '../stores/ui';
import { usePlayerUIStore } from '../stores/player';
import { usePlaybackQueueStore } from '../stores/playbackQueue';
import { usePlayerControls, usePlaybackState } from '../api/hooks';
import { playHookmodeTrack, buildSmartPlaybackParams } from '../lib/keyboard-helpers';

export function useKeyboardShortcuts() {
  const workbench = useWorkbenchStore();
  const selection = useSelectionStore();
  const ui = useUIStore();
  const playerUI = usePlayerUIStore();
  const playbackQueue = usePlaybackQueueStore();
  const { previewMode, setPreviewMode, clearHookTimer } = playbackQueue;
  // Only poll playback state when player is active to avoid unnecessary API calls
  const isPlayerActive = playerUI.isPlayerActive;
  const { data: playbackState } = usePlaybackState({ enabled: isPlayerActive });
  const isPlaying = playbackState?.isPlaying ?? false;
  const hasDevice = !!playbackState?.device;
  const { play, pause, next, previous } = usePlayerControls();

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Don't trigger shortcuts in input fields (except Escape)
      const target = e.target;
      const isInputField =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        (target instanceof HTMLElement && target.isContentEditable);

      if (isInputField && e.key !== 'Escape') {
        return;
      }

      // Detect platform (Mac vs Windows/Linux)
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const ctrlKey = isMac ? e.metaKey : e.ctrlKey;

      // === UNDO/REDO ===
      if (ctrlKey && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (workbench.canUndo) {
          workbench.undo();
          ui.showToast('Undo', 'info');
        }
        return;
      }

      if (ctrlKey && e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        if (workbench.canRedo) {
          workbench.redo();
          ui.showToast('Redo', 'info');
        }
        return;
      }

      // === SELECT ALL ===
      if (ctrlKey && e.key === 'a') {
        e.preventDefault();
        const allTrackIds = workbench.tracks.map((t: { id: string }) => t.id);
        selection.selectAll(allTrackIds);
        ui.showToast(`Selected ${allTrackIds.length} tracks`, 'info');
        return;
      }

      // === DELETE SELECTED TRACKS ===
      if ((e.key === 'Delete' || e.key === 'Backspace') && !isInputField) {
        e.preventDefault();
        const selectedIds = selection.getSelectedIds();
        if (selectedIds.length > 0) {
          selectedIds.forEach((id) => {
            workbench.removeTrack(id);
          });
          selection.clearSelection();
          ui.showToast(`Removed ${selectedIds.length} track(s)`, 'success');
        }
        return;
      }

      // === FOCUS SEARCH (Cmd/Ctrl+F) ===
      if (ctrlKey && e.key === 'f') {
        e.preventDefault();
        // Switch to search tab
        ui.setRightPanelTab('search');
        ui.setRightPanelVisible(true);
        // Try to focus search input
        setTimeout(() => {
          const searchInput = document.querySelector<HTMLInputElement>(
            'input[type="search"], input[placeholder*="search" i]'
          );
          searchInput?.focus();
        }, 100);
        return;
      }

      // === REORDER MODAL (Cmd/Ctrl+R) ===
      if (ctrlKey && e.key === 'r') {
        e.preventDefault();
        ui.setReorderModalOpen(true);
        return;
      }

      // === TAB SWITCHING (1, 2, 3) ===
      if (['1', '2', '3'].includes(e.key) && !ctrlKey && !e.shiftKey && !isInputField) {
        e.preventDefault();
        const tabs = ['search', 'recs', 'playlist'] as const;
        const tabIndex = parseInt(e.key) - 1;
        const selectedTab = tabs[tabIndex];
        if (selectedTab) {
          ui.setRightPanelTab(selectedTab);
          ui.setRightPanelVisible(true);
          ui.showToast(`Switched to ${selectedTab} tab`, 'info');
        }
        return;
      }

      // === ESCAPE - Clear selection / Close modals ===
      if (e.key === 'Escape') {
        // Close modals first (highest priority)
        if (ui.reorderModalOpen) {
          ui.setReorderModalOpen(false);
          return;
        }
        if (ui.smartSlotModalOpen) {
          ui.setSmartSlotModalOpen(false);
          return;
        }
        if (ui.exportModalOpen) {
          ui.setExportModalOpen(false);
          return;
        }
        if (ui.settingsModalOpen) {
          ui.setSettingsModalOpen(false);
          return;
        }
        if (ui.shortcutsVisible) {
          ui.toggleShortcuts();
          return;
        }

        // Clear selection
        if (selection.getSelectionCount() > 0) {
          selection.clearSelection();
          ui.showToast('Selection cleared', 'info');
          return;
        }

        // Blur active input
        if (isInputField && target instanceof HTMLElement) {
          target.blur();
          return;
        }
      }

      // === SPACE - Pure Play/Pause toggle ===
      if ((e.key === ' ' || e.code === 'Space') && !isInputField) {
        e.preventDefault();

        // If playing, pause
        if (isPlaying) {
          pause.mutate();
          clearHookTimer(); // Clear hookmode timer if active
          ui.showToast('Paused', 'info');
          return;
        }

        // Check if device is available before playing
        if (!hasDevice) {
          playerUI.setIsPlayerActive(true);
          playerUI.setPendingPlayAction(() => {
            play.mutate(undefined);
            ui.showToast('Resuming playback', 'info');
          });
          playerUI.setShowDeviceSelector(true);
          ui.showToast('No device selected', 'warning');
          return;
        }

        // Not playing - resume playback
        play.mutate(undefined);
        ui.showToast('Resuming playback', 'info');
        return;
      }

      // === ENTER - Play from selected track forward ===
      if (e.key === 'Enter' && !isInputField) {
        e.preventDefault();

        const selectedIds = selection.getSelectedIds();
        if (selectedIds.length === 0) {
          ui.showToast('No track selected', 'warning');
          return;
        }

        // Get first selected track's position in workbench
        const firstSelectedId = selectedIds[0];
        const allInstanceIds = workbench.tracks.map((t) => t.instanceId);
        const trackIndex = allInstanceIds.indexOf(firstSelectedId!);

        if (trackIndex === -1) {
          ui.showToast('Track not found', 'error');
          return;
        }

        // Build queue from selected track to end
        const queueItems = workbench.tracks.slice(trackIndex).map((t) => ({
          uri: t.uri,
          id: t.id,
          durationMs: t.durationMs,
        }));

        // Smart playback: use contextUri+offset when list is unchanged (fast)
        // Falls back to uris[] when list has been modified (preserves order)
        const playlistUri = workbench.activePlaylist?.uri;
        const isUnchanged = workbench.isOrderUnchanged();
        const playbackParams = buildSmartPlaybackParams(
          playlistUri,
          trackIndex,
          queueItems.map((i) => i.uri),
          isUnchanged
        );

        // Check if device is available
        if (!hasDevice) {
          playerUI.setIsPlayerActive(true);
          playerUI.setPendingPlayAction(() => {
            // In hookmode, we manage queue ourselves (isContextMode=false)
            const contextMode = previewMode === 'hook' ? false : playbackParams.isContextMode;
            playbackQueue.setQueue(queueItems, 0, 'from-track', contextMode);
            if (previewMode === 'hook') {
              const firstItem = queueItems[0];
              if (firstItem) {
                playHookmodeTrack(firstItem, playerUI, playbackQueue, { play, pause });
              }
            } else {
              play.mutate(playbackParams);
            }
            ui.showToast(`Playing from track ${trackIndex + 1}`, 'success');
          });
          playerUI.setShowDeviceSelector(true);
          ui.showToast('No device selected', 'warning');
          return;
        }

        // Activate player and start playback
        playerUI.setIsPlayerActive(true);
        // In hookmode, we manage queue ourselves (isContextMode=false)
        const contextMode = previewMode === 'hook' ? false : playbackParams.isContextMode;
        playbackQueue.setQueue(queueItems, 0, 'from-track', contextMode);

        if (previewMode === 'hook') {
          const firstItem = queueItems[0];
          if (firstItem) {
            playHookmodeTrack(firstItem, playerUI, playbackQueue, { play, pause });
          }
        } else {
          play.mutate(playbackParams);
        }
        ui.showToast(`Playing from track ${trackIndex + 1}${isUnchanged ? '' : ' (custom order)'}`, 'success');
        return;
      }

      // === ARROW LEFT - Previous Track ===
      if (e.key === 'ArrowLeft' && !isInputField) {
        e.preventDefault();

        // Check if device is available
        if (!hasDevice) {
          playerUI.setIsPlayerActive(true);
          playerUI.setPendingPlayAction(() => {
            previous.mutate();
            ui.showToast('Previous track', 'info');
          });
          playerUI.setShowDeviceSelector(true);
          ui.showToast('No device selected', 'warning');
          return;
        }

        if (previewMode === 'hook' && playbackQueue.items.length > 0) {
          // Hookmode: Move to previous hook
          const prevItem = playbackQueue.movePrevious();
          if (prevItem) {
            playHookmodeTrack(prevItem, playerUI, playbackQueue, { play, pause });
            ui.showToast('Previous hook', 'info');
          } else {
            ui.showToast('Already at first hook', 'info');
          }
        } else if (playbackQueue.isContextMode) {
          // Context mode: Spotify manages the queue
          previous.mutate();
          ui.showToast('Previous track', 'info');
        } else if (playbackQueue.items.length > 0) {
          // Manual queue: Move to previous track
          const prevItem = playbackQueue.movePrevious();
          if (prevItem) {
            play.mutate({ uris: [prevItem.uri] });
            ui.showToast('Previous track', 'info');
          } else {
            previous.mutate();
            ui.showToast('Previous track', 'info');
          }
        } else {
          // No queue: Use Spotify's previous
          previous.mutate();
          ui.showToast('Previous track', 'info');
        }
        return;
      }

      // === ARROW RIGHT - Next Track ===
      if (e.key === 'ArrowRight' && !isInputField) {
        e.preventDefault();

        // Check if device is available
        if (!hasDevice) {
          playerUI.setIsPlayerActive(true);
          playerUI.setPendingPlayAction(() => {
            next.mutate();
            ui.showToast('Next track', 'info');
          });
          playerUI.setShowDeviceSelector(true);
          ui.showToast('No device selected', 'warning');
          return;
        }

        if (previewMode === 'hook' && playbackQueue.items.length > 0) {
          // Hookmode: Move to next hook
          const nextItem = playbackQueue.moveNext();
          if (nextItem) {
            playHookmodeTrack(nextItem, playerUI, playbackQueue, { play, pause });
            ui.showToast('Next hook', 'info');
          } else {
            ui.showToast('Already at last hook', 'info');
          }
        } else if (playbackQueue.isContextMode) {
          // Context mode: Spotify manages the queue
          next.mutate();
          ui.showToast('Next track', 'info');
        } else if (playbackQueue.items.length > 0) {
          // Manual queue: Move to next track
          const nextItem = playbackQueue.moveNext();
          if (nextItem) {
            play.mutate({ uris: [nextItem.uri] });
            ui.showToast('Next track', 'info');
          } else {
            next.mutate();
            ui.showToast('Next track', 'info');
          }
        } else {
          // No queue: Use Spotify's next
          next.mutate();
          ui.showToast('Next track', 'info');
        }
        return;
      }

      // === H - Toggle Hookmode ===
      if (e.key === 'h' && !ctrlKey && !isInputField) {
        e.preventDefault();
        const newMode = previewMode === 'hook' ? 'full' : 'hook';
        setPreviewMode(newMode);
        ui.showToast(
          newMode === 'hook' ? 'Hookmode enabled' : 'Hookmode disabled',
          'info'
        );
        return;
      }

      // === ARROW UP - Previous Track in List ===
      if (e.key === 'ArrowUp' && !isInputField) {
        e.preventDefault();
        const allTrackIds = workbench.tracks.map((t: { id: string }) => t.id);
        const selectedIds = selection.getSelectedIds();

        if (allTrackIds.length === 0) {
          return;
        }

        // Get currently selected track (first one if multiple selected)
        const currentId = selectedIds.length > 0 ? selectedIds[0] : null;

        if (currentId) {
          const currentIndex = allTrackIds.indexOf(currentId);
          if (currentIndex > 0) {
            const prevId = allTrackIds[currentIndex - 1];
            if (prevId) {
              selection.selectOnly(prevId);

              // Scroll track into view
              setTimeout(() => {
                const trackElement = document.querySelector(`[data-track-id="${prevId}"]`);
                trackElement?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
              }, 50);

              ui.showToast('Selected previous track', 'info');
            }
          } else {
            ui.showToast('Already at first track', 'info');
          }
        } else {
          // No selection: Select first track
          const firstId = allTrackIds[0];
          if (firstId) {
            selection.selectOnly(firstId);
            ui.showToast('Selected first track', 'info');
          }
        }
        return;
      }

      // === ARROW DOWN - Next Track in List ===
      if (e.key === 'ArrowDown' && !isInputField) {
        e.preventDefault();
        const allTrackIds = workbench.tracks.map((t: { id: string }) => t.id);
        const selectedIds = selection.getSelectedIds();

        if (allTrackIds.length === 0) {
          return;
        }

        // Get currently selected track (first one if multiple selected)
        const currentId = selectedIds.length > 0 ? selectedIds[0] : null;

        if (currentId) {
          const currentIndex = allTrackIds.indexOf(currentId);
          if (currentIndex < allTrackIds.length - 1) {
            const nextId = allTrackIds[currentIndex + 1];
            if (nextId) {
              selection.selectOnly(nextId);

              // Scroll track into view
              setTimeout(() => {
                const trackElement = document.querySelector(`[data-track-id="${nextId}"]`);
                trackElement?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
              }, 50);

              ui.showToast('Selected next track', 'info');
            }
          } else {
            ui.showToast('Already at last track', 'info');
          }
        } else {
          // No selection: Select first track
          const firstId = allTrackIds[0];
          if (firstId) {
            selection.selectOnly(firstId);
            ui.showToast('Selected first track', 'info');
          }
        }
        return;
      }

      // === QUESTION MARK - Show keyboard shortcuts ===
      if (e.key === '?' && !ctrlKey && !isInputField) {
        e.preventDefault();
        ui.toggleShortcuts();
        return;
      }
    };

    // Add event listener
    window.addEventListener('keydown', handler);

    // Cleanup
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, [workbench, selection, ui, playerUI, playbackQueue, play, pause, next, previous, isPlaying, hasDevice]);
}

/**
 * Keyboard shortcut configuration for documentation/help panel
 */
export const KEYBOARD_SHORTCUTS = [
  {
    category: 'Navigation',
    shortcuts: [
      { keys: ['1', '2', '3'], description: 'Switch right panel tabs' },
      { keys: ['Cmd/Ctrl', 'F'], description: 'Focus search' },
      { keys: ['Arrow Up'], description: 'Select previous track in list' },
      { keys: ['Arrow Down'], description: 'Select next track in list' },
      { keys: ['Escape'], description: 'Clear selection / Close modals' },
    ],
  },
  {
    category: 'Editing',
    shortcuts: [
      { keys: ['Cmd/Ctrl', 'Z'], description: 'Undo' },
      { keys: ['Cmd/Ctrl', 'Shift', 'Z'], description: 'Redo' },
      { keys: ['Cmd/Ctrl', 'A'], description: 'Select all tracks' },
      { keys: ['Delete/Backspace'], description: 'Remove selected tracks' },
    ],
  },
  {
    category: 'Playback',
    shortcuts: [
      { keys: ['Space'], description: 'Play/pause' },
      { keys: ['Enter'], description: 'Play from selected track' },
      { keys: ['Arrow Left'], description: 'Previous track' },
      { keys: ['Arrow Right'], description: 'Next track' },
      { keys: ['H'], description: 'Toggle hookmode' },
    ],
  },
  {
    category: 'Actions',
    shortcuts: [
      { keys: ['Cmd/Ctrl', 'R'], description: 'Open reorder modal' },
      { keys: ['?'], description: 'Show keyboard shortcuts' },
    ],
  },
] as const;
